import { gcAccessToken, gcEnvironment, fetchWrap, getClientOrg } from '../../components/common/utilities/fetchWrap';
import config from '../../config';
export interface ActionDefinition {
  actionId: string;
  actionName: string;
  category: string;
  isActive: boolean;
}
const baseUrl = config.endpoints.endpoint || '';
export const getActionDefinitions = async (): Promise<ActionDefinition[]> => {
  try {
    const token = gcAccessToken();
    const gcEnv = gcEnvironment();
    const url =  `${baseUrl}/action-definitions`;
    const response = await fetchWrap({
      url,
      options: {
        method: 'GET'
      },
      token,
      auth: true,
      cookies: false,
      headerOptions: {
        token: token,
        env: gcEnv,
        tokensource:'purecloud'
      }
    });

    if (response && response.data && response.data.items) {
      return response.data.items;
    }
    return [];
  } catch (error) {
    console.error('Error fetching action definitions:', error);
    // Re-throw the error so it can be handled by the component
    if (error instanceof Error) {
      throw error;
    } else if (error && typeof error === 'object' && 'status' in error) {
      // Handle fetchWrap rejection (StandardResponse object)
      const errorObj = error as any;
      const message = errorObj.data?.message || errorObj.data?.error || `API Error: ${errorObj.status}`;
      throw new Error(message);
    } else {
      throw new Error('Failed to fetch action definitions');
    }
  }
};

export interface LogEntry {
  id: string;
  timestamp: string;
  action: string;
  status: string;
  details: string;
  category: string;
  userId: string;
  user: string;
}

// For demo purposes - would be replaced with actual API call
interface ExportResponse {
  exportId: string;
  downloadUrl: string;
  filename: string;
  recordCount: number;
  expiresAt: string;
}

export const exportLogs = async (tenantId: string, startDate?: string, endDate?: string): Promise<ExportResponse> => {
  try {
    // Build query parameters for date range
    const token = gcAccessToken();
    const gcEnv = gcEnvironment();
    const queryParams: string[] = [];
    if (startDate) {
      queryParams.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      queryParams.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const url = `${baseUrl}/activity-logs/${tenantId}/export${queryString}`;
    
    const response = await fetchWrap({
      url,
      options: {
        method: 'POST'
      },
      token,
      auth: true,
      cookies: false,
      headerOptions: {
        token: token,
        env: gcEnv,
        tokensource:'purecloud'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error exporting logs:', error);
    // Re-throw the error with proper message
    if (error instanceof Error) {
      throw error;
    } else if (error && typeof error === 'object' && 'status' in error) {
      // Handle fetchWrap rejection (StandardResponse object)
      const errorObj = error as any;
      const message = errorObj.data?.message || errorObj.data?.error || `Export failed: ${errorObj.status}`;
      throw new Error(message);
    } else {
      throw new Error('Failed to export logs');
    }
  }
};

export const getActivityLogs = async (filters: {
  startTime: string;
  endTime: string;
  page: number;
  perPage: number;
}): Promise<{
  logs: LogEntry[];
  pagination: { total: number; current: number; perPage: number; hasNextPage: boolean; hasPreviousPage: boolean };
}> => {
  try {
    const tenantId = getClientOrg();
    const token = gcAccessToken();
    const gcEnv = gcEnvironment();
    
    // Build query parameters based on filters
    const queryParams: string[] = [];
    
    // Add start and end time parameters
    if (filters.startTime) {
      // Convert datetime-local format to ISO string
      const startDate = new Date(filters.startTime).toISOString();
      queryParams.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    
    if (filters.endTime) {
      // Convert datetime-local format to ISO string
      const endDate = new Date(filters.endTime).toISOString();
      queryParams.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    
    // Add pagination parameters
    queryParams.push(`pageSize=${filters.perPage}`);
    queryParams.push(`pageNumber=${filters.page}`);
    
    // Construct the full URL
    const url = `${baseUrl}/activity-logs/${tenantId}${queryParams.length > 0 ? '?' + queryParams.join('&') : ''}`;
    
    console.log('Fetching logs from:', url);
    
    // Make the API request
    const response = await fetchWrap({
      url,
      options: {
        method: 'GET'
      },
      token,
      auth: true,
      cookies: false,
      headerOptions: {
        token: token,
        env: gcEnv,
        tokensource:'purecloud'
      }
    });
    
    // Process the response based on the expected format:
    // {"items":[], "count": 25, "pagination": {...}}
    if (response && response.data) {
      console.log('API Response:', response.data);
      
      // Extract data from the response using the specified format
      const { items = [], pagination = {} } = response.data;
      
      // Use pagination info from response if available
      const paginationInfo = pagination || {};
      
      // Format current date for timestamps
      const now = new Date();
      const formattedTimestamp = now.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      // Map API response to our internal LogEntry format
      const logs = items.map((item: any, index: number) => {
        // Format timestamp from API or use current time
        let formattedTs = formattedTimestamp;
        if (item.timestamp) {
          try {
            const date = new Date(item.timestamp);
            formattedTs = date.toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false
            });
          } catch (e) {
            // Use formatted timestamp as fallback
            console.log("Error", e);
          }
        }
        
        // Helper function to format user display
        const formatUser = (userId: string) => {
          if (!userId) return 'System';
          return userId;
        };

        const logEntry = {
          id: item.recordId || item.id || item.logId || String(index + 1),
          timestamp: formattedTs,
          action: item.actionName || item.name || item.actionId || 'Unknown Action',
          status: item.status || 'INFO',
          details: item.details || item.message || item.description || 'No details available',
          category: item.actionCategory || item.category || 'Unknown',
          userId: item.userId || '',
          user: formatUser(item.userId)
        };
        
        return logEntry;
      });
      
      // If we received no logs but there should be some according to pagination
      if (logs.length === 0 && paginationInfo.totalItems > 0) {
        console.log('No logs in current response but pagination indicates there should be some');
      }
      
      return {
        logs,
        pagination: {
          total: response.data.count || 0,
          current: paginationInfo.currentPage || filters.page,
          perPage: paginationInfo.pageSize || filters.perPage,
          hasNextPage: paginationInfo.hasNextPage || false,
          hasPreviousPage: paginationInfo.hasPreviousPage || false
        }
      };
    }
    
    throw new Error('Invalid response from API');
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    
    // Re-throw the error so it can be handled by the component
    if (error instanceof Error) {
      throw error;
    } else if (error && typeof error === 'object' && 'status' in error) {
      // Handle fetchWrap rejection (StandardResponse object)
      const errorObj = error as any;
      const message = errorObj.data?.message || errorObj.data?.error || `API Error: ${errorObj.status}`;
      throw new Error(message);
    } else {
      throw new Error('Failed to fetch activity logs');
    }
  }
};